/**
 * Hook para gerenciar lista de instâncias (provider_instances) do tenant
 * 
 * ARQUITETURA:
 * - Instâncias são os "motores de mensagem" que podem ser vinculados a inboxes
 * - Cada instância está associada a um provider, plano contratado e pagamento
 * - Instâncias podem ser vinculadas/desvinculadas de inboxes manualmente
 * - Usa React Query para cache e estado
 * - Usa Supabase Realtime para atualizações instantâneas
 * 
 * @param tenantId - ID do tenant para filtrar as instâncias
 * @returns Query com lista de instâncias e funções de mutação
 */
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para este hook
const logger = createLogger('useInstances');

// Status possíveis de uma instância
export type InstanceStatus = 'pending' | 'released' | 'in_use' | 'cancelled' | 'suspended';

// Status de pagamento
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'processing';

// Origem da instância
export type SourceType = 'plan' | 'addon' | 'manual';

/**
 * Interface para instância com dados do provider e inbox vinculada
 */
export interface Instance {
  id: string;
  instance_name: string;
  /** @deprecated Removido na v1.1.0 - usar provider_display_name */
  provider_id: string;
  /** @deprecated Removido na v1.1.0 - não mais exposto pela RPC */
  provider_slug: string;
  provider_display_name: string;
  provider_logo_url?: string;
  channel_type: string;
  status: InstanceStatus;
  payment_status: PaymentStatus;
  source_type: SourceType;
  inbox_id?: string;
  inbox_name?: string;
  inbox_status?: string;
  created_at: string;
  released_at?: string;
  used_at?: string;
  // Campos de subscription (v1.1.0: sem expor IDs completos)
  has_subscription?: boolean;
  has_tenant_addon?: boolean;
  /** @deprecated Removido na v1.1.0 - usar has_subscription */
  stripe_subscription_id?: string;
  /** @deprecated Removido na v1.1.0 - usar has_tenant_addon */
  tenant_addon_id?: string;
  addon_name?: string;
}

/**
 * Busca instâncias do tenant atual com Supabase Realtime
 * Atualizações são recebidas instantaneamente via WebSocket
 * 
 * @param tenantId - ID do tenant para filtrar as instâncias
 * @returns Query com lista de instâncias e funções de mutação
 */
export function useInstances(tenantId: string | undefined) {
  const queryClient = useQueryClient();

  // Query para buscar instâncias
  // Query via RPC server-side (sem expor dados sensíveis no network)
  const query = useQuery({
    queryKey: ['instances', tenantId],
    queryFn: async (): Promise<Instance[]> => {
      if (!tenantId) return [];

      logger.debug('Buscando instâncias via RPC segura', { tenantId });

      const { data, error } = await supabase.rpc('get_tenant_instances_safe', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar instâncias', { error });
        throw error;
      }

      // Mapeia retorno da RPC para o formato esperado pelo frontend
      const formattedInstances: Instance[] = (data || []).map((instance: any) => ({
        id: instance.id,
        instance_name: instance.instance_name,
        provider_id: '', // Não mais exposto pela RPC (segurança)
        provider_slug: '', // Não mais exposto pela RPC (segurança)
        provider_display_name: instance.provider_display_name || 'Desconhecido',
        provider_logo_url: instance.provider_logo_url || undefined,
        channel_type: instance.channel_type || 'whatsapp',
        status: instance.status as InstanceStatus,
        payment_status: instance.payment_status as PaymentStatus,
        source_type: instance.source_type as SourceType,
        inbox_id: instance.inbox_id_masked || undefined,
        inbox_name: instance.inbox_name || undefined,
        inbox_status: instance.inbox_status || undefined,
        created_at: instance.created_at,
        released_at: instance.released_at || undefined,
        used_at: instance.activated_at || undefined,
        // Campos de subscription (v1.1.0: boolean flags, sem IDs completos)
        has_subscription: instance.has_subscription || false,
        has_tenant_addon: instance.has_tenant_addon || false,
        stripe_subscription_id: instance.has_subscription ? 'active' : undefined,
        tenant_addon_id: instance.has_tenant_addon ? 'active' : undefined,
        addon_name: instance.addon_name || undefined,
      }));

      logger.debug('Instâncias carregadas', { count: formattedInstances.length });
      return formattedInstances;
    },
    enabled: !!tenantId,
    // Refetch automático para refletir mudanças feitas fora da aba
    // (ex.: retorno do checkout Stripe após upgrade/downgrade de plano).
    // O canal Realtime central pode não entregar eventos com a aba oculta,
    // portanto reforçamos com refetch on-mount/focus/reconnect.
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  // Realtime removido deste hook - centralizado em useInboxPageRealtime.ts
  // O React Query compartilha cache pela queryKey, então invalidateQueries
  // disparado pelo hook centralizado atualiza todas as instâncias deste hook.

  // Mutation para vincular instância a inbox
  // v1.2.0 — Após o link, se a RPC sinalizar requires_remote_delete=true,
  // chama o provider-proxy para apagar a sessão antiga no servidor WAHA
  // (recriação síncrona de sessão para evitar conflito de session_name).
  const linkInstanceMutation = useMutation({
    mutationFn: async ({ instanceId, inboxId }: { instanceId: string; inboxId: string }) => {
      logger.info('Vinculando instância a inbox', { instanceId, inboxId });

      const { data, error } = await supabase.rpc('link_instance_to_inbox', {
        p_instance_id: instanceId,
        p_inbox_id: inboxId,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;

      // Tipagem do retorno enriquecido v1.2.0/v1.3.0 (campos de session_name + provider).
      const result = data as {
        success: boolean;
        error?: string;
        old_session_name?: string | null;
        new_session_name?: string | null;
        requires_remote_delete?: boolean;
        old_provider_slug?: string | null;
        new_provider_slug?: string | null;
        channel_type_migrated?: boolean;
      } | null;

      if (result && !result.success) {
        throw new Error(result.error || 'Erro ao vincular instância');
      }

      // v1.3.0 — Guard adicional: só faz sentido apagar sessão remota WAHA-like
      // (waha, evolution-api, wuzapi). Para Meta (meta-whatsapp / meta-instagram)
      // não existe "sessão" remota a apagar — qualquer chamada seria órfã.
      const WAHA_LIKE_PROVIDERS = ['waha', 'evolution-api', 'wuzapi'];
      const oldProviderIsWahaLike =
        !result?.old_provider_slug ||
        WAHA_LIKE_PROVIDERS.includes(result.old_provider_slug);

      // v1.2.0 — recriação síncrona da sessão WAHA antiga.
      // Falhas remotas NÃO bloqueiam o vínculo (a inbox já está com novo
      // session_name válido; o QR funcionará normalmente).
      if (result?.requires_remote_delete && result.old_session_name && oldProviderIsWahaLike) {
        try {
          logger.info('Removendo sessão WAHA antiga no servidor', {
            inboxId,
            old_session_name: result.old_session_name,
            new_session_name: result.new_session_name,
          });

          const { data: deleteData, error: deleteError } = await supabase.functions.invoke(
            'provider-proxy',
            {
              body: {
                inbox_id: inboxId,
                action: 'delete_session',
                session_name_override: result.old_session_name,
              },
            }
          );

          if (deleteError) {
            logger.warn('Falha ao apagar sessão WAHA antiga (não bloqueante)', {
              inboxId,
              old_session_name: result.old_session_name,
              error: deleteError,
            });
          } else {
            logger.info('Sessão WAHA antiga removida com sucesso', { inboxId, deleteData });
          }
        } catch (remoteErr) {
          // Erro real ao chamar a edge — apenas registra; vínculo permanece válido.
          logger.warn('Exceção ao apagar sessão WAHA antiga (não bloqueante)', {
            inboxId,
            error: remoteErr,
          });
        }
      }

      return data;
    },
    onSuccess: () => {
      // Toast de sucesso removido - o componente pai (handleInstanceLinked) já exibe a notificação
      queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', tenantId] });
    },
    onError: (error) => {
      logger.error('Erro ao vincular instância', { error });
      toast.error(error instanceof Error ? error.message : 'Erro ao vincular instância');
    },
  });

  // Mutation para desvincular instância de inbox
  // v1.3.0 — Após o unlink, se a RPC sinalizar requires_remote_delete=true,
  // chama o provider-proxy para apagar a sessão correspondente no servidor WAHA
  // (evita acúmulo de sessões órfãs após desvínculo).
  interface UnlinkInstanceResponse {
    success: boolean;
    error?: string;
    inbox_id?: string;
    inbox_name?: string;
    instance_id?: string;
    instance_name?: string;
    message?: string;
    // v1.3.0: campos adicionais para limpeza remota da sessão
    old_session_name?: string | null;
    requires_remote_delete?: boolean;
  }

  const unlinkInstanceMutation = useMutation({
    mutationFn: async (inboxId: string) => {
      logger.info('Desvinculando instância da inbox', { inboxId });

      // Chamada RPC com tipagem explícita (função criada via migration, pode não estar nos tipos gerados)
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_inbox_id: string; p_tenant_hint: string | null }
      ) => Promise<{ data: UnlinkInstanceResponse | null; error: Error | null }>)(
        'unlink_instance_from_inbox',
        { p_inbox_id: inboxId, p_tenant_hint: tenantId }
      );

      if (error) {
        logger.error('Erro ao desvincular instância', { error });
        throw error;
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Erro ao desvincular instância');
      }

      // v1.3.0 — remoção síncrona da sessão WAHA antiga.
      // Falhas remotas NÃO bloqueiam o desvínculo (a instância já foi liberada
      // no banco; a sessão órfã pode ser limpa em uma operação futura).
      if (data?.requires_remote_delete && data.old_session_name) {
        try {
          logger.info('Removendo sessão WAHA antiga no servidor (unlink)', {
            inboxId,
            old_session_name: data.old_session_name,
          });

          const { data: deleteData, error: deleteError } = await supabase.functions.invoke(
            'provider-proxy',
            {
              body: {
                inbox_id: inboxId,
                action: 'delete_session',
                session_name_override: data.old_session_name,
              },
            }
          );

          if (deleteError) {
            logger.warn('Falha ao apagar sessão WAHA antiga no unlink (não bloqueante)', {
              inboxId,
              old_session_name: data.old_session_name,
              error: deleteError,
            });
          } else {
            logger.info('Sessão WAHA antiga removida com sucesso (unlink)', { inboxId, deleteData });
          }
        } catch (remoteErr) {
          logger.warn('Exceção ao apagar sessão WAHA antiga no unlink (não bloqueante)', {
            inboxId,
            error: remoteErr,
          });
        }
      }

      return data;
    },
    onSuccess: () => {
      toast.success('Instância desvinculada! Agora está disponível para reutilização.');
      queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', tenantId] });
    },
    onError: (error) => {
      // v1.4.0: expõe a mensagem real vinda da RPC (ex.: status que impede o desvínculo).
      // Os erros de negócio (P0001–P0004) fazem RAISE no Postgres, então o log de auditoria
      // sofre rollback — por isso registramos o contexto completo aqui no console.
      const pgError = error as { message?: string; code?: string; details?: string };
      logger.error('Erro ao desvincular instância', {
        code: pgError?.code,
        message: pgError?.message,
        details: pgError?.details,
      });
      toast.error('Erro ao desvincular instância', {
        description: pgError?.message || 'Tente novamente em instantes.',
      });
    },

  });

  // ---------------------------------------------------------------------------
  // Mutation para EXCLUIR definitivamente uma instância (v1.5.0)
  // Só é permitida para instâncias não vinculadas a inbox (validação na RPC).
  // Após excluir, a cota é liberada e o usuário pode criar outra instância pelo
  // fluxo padrão. Limpeza da sessão remota (WAHA-like) é best-effort.
  // ---------------------------------------------------------------------------
  interface DeleteInstanceResponse {
    success: boolean;
    instance_name?: string;
    provider_slug?: string | null;
    session_name?: string | null;
  }

  const deleteInstanceMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      logger.info('Excluindo instância', { instanceId });

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_instance_id: string; p_tenant_hint: string | null }
      ) => Promise<{ data: DeleteInstanceResponse | null; error: Error | null }>)(
        'delete_provider_instance',
        { p_instance_id: instanceId, p_tenant_hint: tenantId ?? null }
      );

      if (error) throw error;

      return data;
    },
    onSuccess: () => {
      toast.success('Instância excluída! Você já pode criar uma nova.');
      queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-instance-quota', tenantId] });
    },
    onError: (error) => {
      const pgError = error as { message?: string; code?: string; details?: string };
      logger.error('Erro ao excluir instância', {
        code: pgError?.code,
        message: pgError?.message,
        details: pgError?.details,
      });
      toast.error('Erro ao excluir instância', {
        description: pgError?.message || 'Tente novamente em instantes.',
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Mutation para REATIVAR uma instância cancelada (v1.6.0)
  // A RPC `reactivate_cancelled_instance` valida ownership + cota disponível e
  // PRESERVA credenciais/sessão — com inbox vinculada, volta como 'in_use'
  // sem exigir novo QR code.
  // ---------------------------------------------------------------------------
  interface ReactivateInstanceResponse {
    success: boolean;
    instance_id?: string;
    new_status?: 'in_use' | 'released';
    inbox_preserved?: boolean;
  }

  const reactivateInstanceMutation = useMutation({
    mutationFn: async (instanceId: string) => {
      logger.info('Reativando instância cancelada', { instanceId });

      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_instance_id: string; p_tenant_hint: string | null }
      ) => Promise<{ data: ReactivateInstanceResponse | null; error: Error | null }>)(
        'reactivate_cancelled_instance',
        { p_instance_id: instanceId, p_tenant_hint: tenantId ?? null }
      );

      if (error) throw error;

      return data;
    },
    onSuccess: (data) => {
      // Mensagem contextual: com inbox preservada, avisa que não precisa reconectar
      toast.success(
        data?.inbox_preserved
          ? 'Instância reativada! A inbox voltou conectada, sem novo QR code.'
          : 'Instância reativada! Ela está disponível para vincular a uma inbox.'
      );
      queryClient.invalidateQueries({ queryKey: ['instances', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-instance-quota', tenantId] });
    },
    onError: (error) => {
      // Expõe a mensagem real da RPC (ex.: sem cota disponível para o provider)
      const pgError = error as { message?: string; code?: string; details?: string };
      logger.error('Erro ao reativar instância', {
        code: pgError?.code,
        message: pgError?.message,
        details: pgError?.details,
      });
      toast.error('Erro ao reativar instância', {
        description: pgError?.message || 'Tente novamente em instantes.',
      });
    },
  });

  // Estatísticas calculadas
  const instances = query.data || [];
  const stats = {
    total: instances.length,
    available: instances.filter(i => i.status === 'released').length,
    inUse: instances.filter(i => i.status === 'in_use').length,
    pending: instances.filter(i => i.status === 'pending').length,
    cancelled: instances.filter(i => i.status === 'cancelled').length,
    suspended: instances.filter(i => i.status === 'suspended').length,
  };

  // Função auxiliar para buscar instância por ID
  const getInstanceById = (instanceId: string) => {
    return instances.find(i => i.id === instanceId);
  };

  // Função auxiliar para buscar instância vinculada a uma inbox
  const getInstanceByInboxId = (inboxId: string) => {
    return instances.find(i => i.inbox_id === inboxId);
  };

  // Função auxiliar para buscar instâncias disponíveis por tipo de canal
  const getAvailableInstancesByChannel = (channelType: string) => {
    return instances.filter(i => 
      i.status === 'released' && 
      i.channel_type === channelType
    );
  };

  return {
    ...query,
    instances,
    stats,
    linkInstance: linkInstanceMutation.mutateAsync,
    unlinkInstance: unlinkInstanceMutation.mutateAsync,
    deleteInstance: deleteInstanceMutation.mutateAsync,
    reactivateInstance: reactivateInstanceMutation.mutateAsync,
    isLinking: linkInstanceMutation.isPending,
    isUnlinking: unlinkInstanceMutation.isPending,
    isDeleting: deleteInstanceMutation.isPending,
    isReactivating: reactivateInstanceMutation.isPending,
    getInstanceById,
    getInstanceByInboxId,
    getAvailableInstancesByChannel,
  };
}
